import type { ApiAddressesGetItem } from '@/shared/types/api/addresses';

// The fields a recipient pick writes onto a form. The picker stays selected
// only while the form's current values still match the picked recipient on
// every one of these fields; a genuine user edit diverges one of them and
// clears the select. Order is irrelevant (matching uses `every`).
export const RECIPIENT_FIELDS = [
  'address_name',
  'region',
  'city',
  'street',
  'building',
  'apartment',
  'additional_info',
  'first_name',
  'last_name',
  'middle_name',
  'phone_number',
  'passport_series',
  'pinfl',
] as const;

export type RecipientField = (typeof RECIPIENT_FIELDS)[number];

// Normalize null/undefined to '' so a saved recipient with null fields compares
// equal to the corresponding empty form fields after applyRecipient runs.
export const norm = (value: unknown): string => (value == null ? '' : String(value));

export const recipientMatchesValues = (
  recipient: ApiAddressesGetItem,
  values: Partial<Record<RecipientField, unknown>> | undefined,
  fields: readonly RecipientField[] = RECIPIENT_FIELDS,
): boolean => {
  if (!values) return false;
  return fields.every((key) => norm(recipient[key]) === norm(values[key]));
};

export const formatFullName = (r: ApiAddressesGetItem, fallback: string): string => {
  const first = r.first_name?.trim() ?? '';
  const last = r.last_name?.trim() ?? '';
  const joined = [last, first].filter(Boolean).join(' ').trim();
  return joined || fallback;
};

// Writes a saved recipient's fields via the provided setter. The setter maps a
// base field name to the concrete form field (top-level for the parcel form,
// `targets.${index}.<field>` for splits) and applies the RHF setValue options.
//
// No shouldValidate: eager validation here ran the zod resolver once per
// setValue (13x, whole-schema each time) synchronously inside the select's
// change handler — a heavy re-render cascade that disrupted focus/layout and
// jumped the page to the top. Validation still runs on submit via handleSubmit.
export const applyRecipient = (
  recipient: ApiAddressesGetItem,
  setField: (field: RecipientField, value: string) => void,
): void => {
  setField('address_name', recipient.address_name);
  setField('region', recipient.region);
  setField('city', recipient.city);
  setField('street', recipient.street);
  setField('building', recipient.building);
  setField('apartment', recipient.apartment ?? '');
  setField('additional_info', recipient.additional_info ?? '');
  setField('first_name', recipient.first_name ?? '');
  setField('last_name', recipient.last_name ?? '');
  setField('middle_name', recipient.middle_name ?? '');
  setField('phone_number', recipient.phone_number ?? '');
  setField('passport_series', recipient.passport_series ?? '');
  setField('pinfl', recipient.pinfl ?? '');
};

// Robust guarantee against any residual scroll jump: capture the window scroll
// offset before the fill, then restore it after React commits and the layout
// settles (two rAFs). Only restore if it actually moved.
export const restoreScrollAfterApply = (apply: () => void): void => {
  const { scrollX, scrollY } = window;
  apply();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
        window.scrollTo(scrollX, scrollY);
      }
    });
  });
};
